// Past practice sessions: what was said, what was practised, what it cost.
// Every session is written to english/data/sessions/ as it happens, so this is a
// full record rather than a summary — click one open to reread the conversation.

import { useEffect, useState } from 'react';
import { fetchSessions } from '../../services/englishApi';

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 16).replace('T', ' ');
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function minutes(rec) {
  const a = new Date(rec.started_at).getTime();
  const b = new Date(rec.ended_at).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0;
  return (b - a) / 60000;
}

function usd(n) {
  const v = Number(n) || 0;
  if (v > 0 && v < 0.01) return '<$0.01';
  return `$${v.toFixed(2)}`;
}

export default function SessionHistory({ slug, onClose }) {
  const [sessions, setSessions] = useState(null);
  const [spend, setSpend] = useState(null);
  const [openFile, setOpenFile] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchSessions(slug)
      .then((d) => {
        if (cancelled) return;
        setSessions(d.sessions || []);
        setSpend(d.spend || null);
      })
      .catch(() => !cancelled && setError('Could not load past sessions.'));
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/85 p-6">
      <div className="flex max-h-full w-full max-w-3xl flex-col rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl">
        <div className="flex items-baseline justify-between border-b border-slate-800 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-100">Past sessions</h2>
            {spend && (
              <p className="mt-0.5 text-xs text-slate-500">
                {spend.sessions} session{spend.sessions === 1 ? '' : 's'} ·{' '}
                {Math.round(spend.minutes)} min · {usd(spend.total_usd)} total
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
          >
            Close
          </button>
        </div>

        <div className="thin-scroll flex-1 overflow-y-auto p-3">
          {error && <p className="p-3 text-sm text-rose-300">{error}</p>}
          {!error && sessions === null && (
            <p className="p-3 text-sm text-slate-500">Loading…</p>
          )}
          {sessions?.length === 0 && (
            <p className="p-3 text-sm text-slate-500">
              No sessions yet — they appear here as soon as you start talking.
            </p>
          )}

          {sessions?.map((rec) => {
            const isOpen = openFile === rec.file;
            return (
              <div
                key={rec.file}
                className="mb-2 overflow-hidden rounded-xl border border-slate-800"
              >
                <button
                  type="button"
                  onClick={() => setOpenFile(isOpen ? null : rec.file)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-800/50"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-slate-200">
                      {fmtDate(rec.started_at)}
                      <span className="ml-2 text-xs text-slate-500">{rec.slug}</span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500">
                      {minutes(rec).toFixed(1)} min · {rec.turns?.length || 0} turns ·{' '}
                      {rec.practiced?.length || 0} phrases
                      {rec.corrections?.length
                        ? ` · ${rec.corrections.length} corrections`
                        : ''}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-sm tabular-nums text-slate-300">
                      {usd(rec.cost?.total_usd)}
                    </div>
                    <div className="text-[10px] text-slate-600">
                      {isOpen ? 'hide' : 'open'}
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-slate-800 bg-slate-950/40 p-3">
                    {rec.summary?.summary && (
                      <p className="mb-3 rounded-lg bg-slate-900 px-3 py-2 text-xs leading-relaxed text-slate-300">
                        {rec.summary.summary}
                      </p>
                    )}

                    <div className="space-y-2">
                      {(rec.turns || []).map((t, i) => (
                        <div
                          key={i}
                          className={`flex ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-xs leading-relaxed ${
                              t.role === 'user'
                                ? 'rounded-br-sm bg-sky-600/20 text-sky-100'
                                : 'rounded-bl-sm bg-slate-800 text-slate-200'
                            }`}
                          >
                            {t.text}
                          </div>
                        </div>
                      ))}
                    </div>

                    {rec.corrections?.length > 0 && (
                      <div className="mt-3 border-t border-slate-800 pt-2">
                        <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                          Corrections
                        </h4>
                        {rec.corrections.map((c, i) => (
                          <div key={i} className="mb-1 text-xs">
                            <span className="text-rose-300/70 line-through">{c.original}</span>
                            <span className="mx-1.5 text-slate-600">→</span>
                            <span className="text-emerald-300">{c.corrected}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {rec.practiced?.length > 0 && (
                      <div className="mt-3 border-t border-slate-800 pt-2 text-[11px] text-slate-500">
                        <span className="font-semibold uppercase tracking-wider">
                          Practised:
                        </span>{' '}
                        {rec.practiced.join(' · ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
